import type { ReactNode } from 'react';
import { cx } from './cx';

interface C1DashboardProps {
  /** The main column: the figures and charts the page is about. */
  children: ReactNode;
  /**
   * The side rail — a snapshot, announcements, system status. Omit it and the
   * main column takes the full width, so a page without a rail needs no variant.
   */
  aside?: ReactNode;
  /**
   * Names the rail for a screen reader. Required whenever `aside` is set: a bare
   * `<aside>` is announced as "complementary" with no clue what is in it, and a
   * page with a rail on every screen gives the reader nothing to tell them apart.
   */
  asideLabel?: string;
  /**
   * Pins the rail while the main column scrolls. Off by default — it only helps
   * when the rail is shorter than the main column, and a rail taller than the
   * viewport becomes unreachable when pinned.
   */
  stickyAside?: boolean;
  className?: string;
}

/**
 * The two-column dashboard layout: a main column and an optional side rail.
 *
 * ONE COLUMN UNTIL THERE IS ROOM FOR TWO. The rail drops below the main column
 * under 1100px rather than shrinking with it — a 200px-wide rail holds neither a
 * readable figure nor a legible label, and a dashboard that technically still has
 * two columns on a tablet is worse than one that admits it has one.
 *
 * Source order is main-then-rail, so it also reads in the right order when it
 * stacks and for anyone arriving by keyboard.
 */
export function C1Dashboard({
  children,
  aside,
  asideLabel,
  stickyAside = false,
  className,
}: C1DashboardProps) {
  return (
    <div className={cx('c1-dashboard', Boolean(aside) && 'c1-dashboard--split', className)}>
      <div className="c1-dashboardMain">{children}</div>
      {aside ? (
        <aside
          className={cx('c1-dashboardAside', stickyAside && 'c1-dashboardAside--sticky')}
          aria-label={asideLabel}
        >
          {aside}
        </aside>
      ) : null}
    </div>
  );
}
